import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CampaignCard from '../components/campaigns/CampaignCard';
import CreateCampaignModal from '../components/campaigns/CreateCampaignModal';
import EditCampaignModal from '../components/campaigns/EditCampaignModal';
import { getCampaigns, createCampaign, updateCampaign, deleteCampaign, updateCampaignStatus } from '../utils/apiService';

const Campaigns = () => {
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Load campaigns from API
  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCampaigns();
      setCampaigns(data);
    } catch (err) {
      setError('Failed to load campaigns. Please try again.');
      console.error('Error loading campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load campaigns on component mount
  useEffect(() => {
    loadCampaigns();
  }, []);

  // Handle creating a new campaign
  const handleCreateCampaign = async (newCampaign) => {
    setLoading(true);
    setError(null);
    try {
      const data = await createCampaign(newCampaign);
      setCampaigns(prev => [...prev, data]);
      setShowCreateModal(false);
    } catch (err) {
      setError('Failed to create campaign. Please try again.');
      console.error('Error creating campaign:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a campaign
  const handleDeleteCampaign = async (id) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      setLoading(true);
      setError(null);
      try {
        await deleteCampaign(id);
        setCampaigns(prev => prev.filter(campaign => campaign._id !== id));
      } catch (err) {
        setError('Failed to delete campaign. Please try again.');
        console.error('Error deleting campaign:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle editing a campaign
  const handleEditCampaign = async (updatedCampaign) => {
    setLoading(true);
    setError(null);
    try {
      const data = await updateCampaign(updatedCampaign._id, updatedCampaign);
      setCampaigns(prev => prev.map(c => c._id === data._id ? data : c));
      setEditingCampaign(null);
    } catch (err) {
      setError('Failed to edit campaign. Please try again.');
      console.error('Error editing campaign:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle status change of a campaign
  const handleStatusChange = async (campaignId, newStatus) => {
    setLoading(true);
    setError(null);
    try {
      await updateCampaignStatus(campaignId, newStatus);
      setCampaigns(campaigns.map(c => 
        c._id === campaignId ? { ...c, status: newStatus } : c
      ));
    } catch (err) {
      setError('Failed to update campaign status. Please try again.');
      console.error('Error updating campaign status:', err);
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (campaign) => {
    setEditingCampaign(campaign);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Campaigns</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-primary hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Create Campaign
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {campaigns.map(campaign => (
          <CampaignCard
            key={campaign._id}
            campaign={campaign}
            onDelete={handleDeleteCampaign}
            onEdit={openEditModal}
            onStatusChange={handleStatusChange}
          />
        ))}
      </div>

      {showCreateModal && (
        <CreateCampaignModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateCampaign}
        />
      )}

      {editingCampaign && (
        <EditCampaignModal
          campaign={editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onSuccess={() => {
            setEditingCampaign(null);
            loadCampaigns();
          }}
        />
      )}
    </div>
  );
};

export default Campaigns;
